import React, { ReactElement } from 'react';
import { MdClose } from 'react-icons/md';
import styled from 'styled-components/macro';

// context
import { useReaction } from 'context/reactions/ReactionProvider';
// constants
import {
  VideoReactionsDictionary,
  VideoReactionsDictionaryType,
  VIDEO_REACTION_TYPE,
  VideoComentsDictionary,
} from 'const/VideoReactionDictionary';
// types
import { VideoReactionItem } from 'queries/reactions/types';

const ReactionBar = styled.div<any>`
  position: absolute;
  bottom: ${props => (props.hasControls ? '46px' : '15px')};
  height: 20px;
  width: 100%;
  z-index: 10;
`;

type ReactionPositionProps = {
  position: number;
};

const ReactionPosition = styled.div<ReactionPositionProps>`
  left: ${({ position }) => `${position}%`};
  position: absolute;
  bottom: 0px;
  padding-bottom: 10px;
  width: 10%;
  text-align: center;
`;

type ReactionsBubbleProps = {
  position: number;
};

const ReactionsBubble = styled.div<ReactionsBubbleProps>`
  ${({ position }) => {
    if (position === 0) {
      return {
        left: '16px',
      };
    }
    if (position === 10) {
      return {
        transform: 'translateX(-30%)',
        left: '50%',
      };
    }
    if (position === 80) {
      return {
        transform: 'translateX(-60%)',
        left: '50%',
      };
    }
    if (position === 90) {
      return {
        right: '16px',
      };
    } else {
      return {
        transform: 'translateX(-50%)',
        left: `${position}%`,
      };
    }
  }};
  display: none;
  box-sizing: border-box;
  position: absolute;
  bottom: 20px;
  width: auto;
  min-width: 150px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  box-shadow: 0px 8px 32px rgba(29, 30, 36, 0.08);
  border-radius: 4px;
  padding: 8px 12px;
  flex-direction: column;
  gap: 8px;
  z-index: 999;
  ${ReactionPosition}:hover & {
    display: flex;
  }
  @media screen and (max-width: 768px) {
    ${ReactionPosition}:hover & {
      display: none;
    }
  }
`;

const ReactionItem = styled.div`
  display: block;
  margin-bottom: -10px;
  &:nth-child(n + 15) {
    display: none;
  }
`;

const BubbleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BubbleIcon = styled.div`
  display: flex;
  align-items: center;
  width: 20px;
`;

const BubbleName = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 24px;
  color: #272a32;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BubbleText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  max-width: 300px;
  color: ${props => props.theme.colors.neutral[100]};
`;

const BubbleDelete = styled.div`
  margin-left: auto;
  color: ${props => props.theme.colors.neutral[60]};
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${props => props.theme.colors.danger[100]};
    color: ${props => props.theme.colors.white[100]};
  }
`;

type ReactionWithIcon = VideoReactionItem & {
  icon: ReactElement;
  smallIcon: ReactElement;
};

type Props = {
  videoDetails: any;
  hasControls?: boolean;
};

export const PlayerReactions = ({ videoDetails, hasControls }: Props) => {
  const { reactionsQuery, deleteReaction: removeVideoReaction } = useReaction();
  const videoLengthSeconds = Math.floor(
    (videoDetails?.videoLength || 0) / 1000
  );
  const { data: videoReactions = {} } = reactionsQuery;
  const reactionsPositions: {
    [key: number]: { position: number; reactions: ReactionWithIcon[] };
  } = {};
  for (const reaction of Object.values(videoReactions)) {
    let position =
      Math.round((reaction.playbackPosition / videoLengthSeconds) * 10) * 10;
    position = position > 90 ? 90 : position;
    const icon =
      reaction.type === VIDEO_REACTION_TYPE.COMMENT ? (
        VideoComentsDictionary.icon
      ) : reaction?.emoticonId &&
        VideoReactionsDictionary[
          reaction.emoticonId as keyof VideoReactionsDictionaryType
        ] ? (
        VideoReactionsDictionary[
          reaction.emoticonId as keyof VideoReactionsDictionaryType
        ]?.icon
      ) : (
        <>?</>
      );
    const smallIcon =
      reaction.type === VIDEO_REACTION_TYPE.COMMENT ? (
        VideoComentsDictionary.smallIcon
      ) : reaction?.emoticonId &&
        VideoReactionsDictionary[
          reaction.emoticonId as keyof VideoReactionsDictionaryType
        ] ? (
        VideoReactionsDictionary[
          reaction.emoticonId as keyof VideoReactionsDictionaryType
        ]?.smallIcon
      ) : (
        <>?</>
      );
    const reactionWithIcon = { ...reaction, icon, smallIcon };
    reactionsPositions[position] = !reactionsPositions[position]
      ? { position, reactions: [reactionWithIcon] }
      : {
          ...reactionsPositions[position],
          reactions: [
            ...reactionsPositions[position].reactions,
            reactionWithIcon,
          ],
        };
  }

  return (
    <ReactionBar hasControls={hasControls}>
      {Object.values(reactionsPositions).map(reactionPosition => (
        <ReactionPosition
          key={reactionPosition.position}
          position={reactionPosition.position}
        >
          {reactionPosition.reactions.map(reaction => (
            <ReactionItem key={reaction.reactionId}>
              {reaction.smallIcon}
            </ReactionItem>
          ))}

          <ReactionsBubble position={reactionPosition.position}>
            {reactionPosition.reactions.map(reaction => (
              <BubbleItem key={reaction.reactionId}>
                <BubbleIcon>{reaction.icon}</BubbleIcon>
                <BubbleName>{reaction.name}</BubbleName>
                <BubbleText>{reaction.text || ''}</BubbleText>
                <BubbleDelete
                  onClick={() => removeVideoReaction(reaction.reactionId)}
                >
                  <MdClose />
                </BubbleDelete>
              </BubbleItem>
            ))}
          </ReactionsBubble>
        </ReactionPosition>
      ))}
    </ReactionBar>
  );
};
